'use client';

import React, { useState, useRef, useEffect } from 'react';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export default function Contact() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-on-scroll').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await res.json();

      if (result.success) {
        setSubmitted(true);
      } else {
        setSubmitError(result.error ?? 'Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      label: 'Email',
      value: 'ramsonndanjim@hotmail.com',
      href: 'mailto:ramsonndanjim@hotmail.com',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
    {
      label: 'Phone',
      value: '+32 484 61 69 27',
      href: 'tel:+32484616927',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 11a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
    {
      label: 'Location',
      value: 'Belgium',
      href: null,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor" />
        </svg>
      ),
    },
  ];

  return (
    <section id="contact" className="bg-dark py-24 md:py-32" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-5 md:px-16 lg:px-24">
        {/* Header */}
        <div className="mb-16">
          <span className="section-label animate-on-scroll" style={{ color: '#06b6d4' }}>
            06 — Contact
          </span>
          <h2
            className="section-title mt-4 text-light-warm animate-on-scroll"
            style={{ fontSize: 'clamp(2.5rem, 5vw, 5rem)' }}
          >
            Let&rsquo;s
            <br />
            <span className="text-primary">Connect</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          {/* Left: Contact info */}
          <div className="lg:col-span-4 space-y-8">
            <p
              className="font-serif text-secondary-dark leading-relaxed animate-on-scroll"
              style={{ fontSize: 'clamp(1rem, 1.3vw, 1.15rem)', fontStyle: 'italic', opacity: 0.85 }}
            >
              Open to new opportunities, collaborations, and interesting projects.
              Whether you&rsquo;re an agency, startup, or corporate team — let&rsquo;s talk.
            </p>

            <div className="space-y-6 animate-on-scroll">
              {contactInfo.map((info) => (
                <div key={info.label} className="flex items-start gap-4">
                  <div className="text-primary mt-0.5 flex-shrink-0">{info.icon}</div>
                  <div>
                    <p className="font-condensed text-xs tracking-widest uppercase text-secondary-dark opacity-50 mb-1">
                      {info.label}
                    </p>
                    {info.href ? (
                      <a
                        href={info.href}
                        className="font-sans text-secondary-dark hover:text-primary transition-colors duration-300"
                        style={{ fontSize: '0.95rem' }}
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="font-sans text-secondary-dark" style={{ fontSize: '0.95rem' }}>
                        {info.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* LinkedIn */}
            <div className="animate-on-scroll">
              <a
                href="https://www.linkedin.com/in/ramson-ndanjim"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline-white inline-flex items-center gap-3"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
                LinkedIn Profile
              </a>
            </div>

            {/* Availability badge */}
            <div className="animate-on-scroll flex items-center gap-3 pt-4">
              <span className="w-2 h-2 rounded-full bg-accent-green animate-pulse" />
              <span className="font-condensed text-xs tracking-widest uppercase text-secondary-dark opacity-60">
                Available for new projects
              </span>
            </div>
          </div>

          {/* Right: Contact form */}
          <div className="lg:col-span-8 animate-on-scroll">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full min-h-64 text-center py-16">
                <div className="w-16 h-16 bg-primary flex items-center justify-center mb-6">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h3
                  className="font-condensed font-bold uppercase text-light-warm mb-3"
                  style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)' }}
                >
                  Message Sent!
                </h3>
                <p className="font-sans text-secondary-dark opacity-70">
                  Thank you for reaching out. I&rsquo;ll get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                  {/* Name */}
                  <div
                    className={`border transition-all duration-300 ${
                      focused === 'name' ? 'border-primary' : 'border-secondary-dark'
                    }`}
                    style={{ borderColor: focused === 'name' ? '#7c3aed' : 'rgba(255,255,255,0.15)' }}
                  >
                    <label className="block font-condensed text-xs tracking-widest uppercase px-5 pt-4 pb-1" style={{ color: 'rgba(245,245,243,0.4)' }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => setFocused('name')}
                      onBlur={() => setFocused(null)}
                      placeholder="Your full name"
                      className="w-full bg-transparent px-5 pb-4 font-sans text-light-warm placeholder-secondary-dark outline-none"
                      style={{ fontSize: '0.95rem', opacity: 1, color: '#f5f5f3' }}
                    />
                  </div>

                  {/* Email */}
                  <div
                    className="border-t border-r border-b md:border-l-0 transition-all duration-300"
                    style={{
                      borderColor: focused === 'email' ? '#7c3aed' : 'rgba(255,255,255,0.15)',
                    }}
                  >
                    <label className="block font-condensed text-xs tracking-widest uppercase px-5 pt-4 pb-1" style={{ color: 'rgba(245,245,243,0.4)' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocused('email')}
                      onBlur={() => setFocused(null)}
                      placeholder="your@email.com"
                      className="w-full bg-transparent px-5 pb-4 font-sans text-light-warm placeholder-secondary-dark outline-none"
                      style={{ fontSize: '0.95rem', color: '#f5f5f3' }}
                    />
                  </div>
                </div>

                {/* Subject */}
                <div
                  className="border-b border-l border-r transition-all duration-300"
                  style={{
                    borderColor: focused === 'subject' ? '#7c3aed' : 'rgba(255,255,255,0.15)',
                  }}
                >
                  <label className="block font-condensed text-xs tracking-widest uppercase px-5 pt-4 pb-1" style={{ color: 'rgba(245,245,243,0.4)' }}>
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    onFocus={() => setFocused('subject')}
                    onBlur={() => setFocused(null)}
                    placeholder="What&apos;s this about?"
                    className="w-full bg-transparent px-5 pb-4 font-sans text-light-warm placeholder-secondary-dark outline-none"
                    style={{ fontSize: '0.95rem', color: '#f5f5f3' }}
                  />
                </div>

                {/* Message */}
                <div
                  className="border-b border-l border-r transition-all duration-300"
                  style={{
                    borderColor: focused === 'message' ? '#7c3aed' : 'rgba(255,255,255,0.15)',
                  }}
                >
                  <label className="block font-condensed text-xs tracking-widest uppercase px-5 pt-4 pb-1" style={{ color: 'rgba(245,245,243,0.4)' }}>
                    Message *
                  </label>
                  <textarea
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocused('message')}
                    onBlur={() => setFocused(null)}
                    placeholder="Tell me about your project..."
                    rows={5}
                    className="w-full bg-transparent px-5 pb-4 font-sans text-light-warm placeholder-secondary-dark outline-none resize-none"
                    style={{ fontSize: '0.95rem', color: '#f5f5f3' }}
                  />
                </div>

                {/* Error message */}
                {submitError && (
                  <div className="pt-4">
                    <p className="font-sans text-sm" style={{ color: '#ef4444' }}>
                      {submitError}
                    </p>
                  </div>
                )}

                {/* Submit */}
                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full md:w-auto justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}